import { NetworkType, useFormatAmount, useIntl } from '@capsicum/common';
import { Alert, Button, ButtonSection, useRouter } from '@capsicum/common-web';
import { AlertIcon } from '@capsicum/common-web/icons';
import {
	CURRENCY_SHORTCODES,
	CurrencyType,
	RECEIVE_NETWORK_LABELS,
	RECEIVE_NETWORK_SHORTCODES,
	type ReceiveNetworks,
	TypeCurrency,
	useWalletWithdrawalMinimumAmount,
	useWithdrawDataAtom
} from '@capsicum/features/wallet/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { type NetworkCardItem, NetworkSelectionListCard } from '../../ui';
import { S } from '../styles';

const networks: Array<NetworkCardItem> = [
	{
		label: RECEIVE_NETWORK_LABELS[NetworkType.CC2 as ReceiveNetworks],
		name: NetworkType.CC2 as ReceiveNetworks
	},
	{
		isNotSupported: true,
		label: RECEIVE_NETWORK_LABELS[NetworkType.ETHEREUM as ReceiveNetworks],
		name: NetworkType.ETHEREUM as ReceiveNetworks,
		shortCode: RECEIVE_NETWORK_SHORTCODES[NetworkType.ETHEREUM as ReceiveNetworks]
	}
];

export const NetworkCHZ = () => {
	const { push } = useRouter();
	const [selectedNetwork, setSelectedNetwork] = useState<string | undefined>(undefined);
	const [isButtonDisabled, setButtonDisabled] = useState<boolean>(true);
	const { formatAmount } = useFormatAmount();
	const { setSelectedNetworkType } = useWithdrawDataAtom();
	const { isLoading: minCHZLoading, minAmount: minCHZ } = useWalletWithdrawalMinimumAmount(TypeCurrency.Crypto);

	const getNetworkStatus = useCallback(
		(networkId: string) => {
			if (selectedNetwork === networkId) {
				return 'selected';
			}

			return 'unselected';
		},
		[selectedNetwork]
	);

	useEffect(() => {
		if (!selectedNetwork) {
			return;
		}
		setButtonDisabled(false);
	}, [selectedNetwork, setSelectedNetwork]);

	const { $t } = useIntl();

	const title = $t('wallet.withdraw.networks.title');
	const warning = $t('wallet.withdraw.networks.alertCHZ');
	const button = $t('wallet.withdraw.networks.button');

	const networkCards = useMemo(
		() =>
			networks.map(network => {
				const networkWithMinimum = {
					...network,
					isMinAmountLoading: minCHZLoading,
					minAmount: `${formatAmount(minCHZ)} ${CURRENCY_SHORTCODES[CurrencyType.CHILIZ]}`
				};

				return (
					<NetworkSelectionListCard
						item={networkWithMinimum}
						key={networkWithMinimum.name}
						selectNetwork={setSelectedNetwork}
						status={getNetworkStatus(networkWithMinimum.name)}
					/>
				);
			}),
		[formatAmount, getNetworkStatus, minCHZ, minCHZLoading]
	);

	return (
		<S.Container>
			<S.Title>{title}</S.Title>
			{networkCards}
			<Alert
				closable={false}
				icon={AlertIcon}
				subTitle={warning}
				testID="withdrawCHZ-selectNetwork-warning"
				title=""
				type="important"
			/>
			<ButtonSection>
				<Button
					color="brand"
					disabled={minCHZLoading || isButtonDisabled}
					isLoading={minCHZLoading}
					testID="withdrawCHZ-selectNetwork-next"
					text={button}
					variant="default"
					onClick={() => {
						setSelectedNetworkType(selectedNetwork || '');
						push(`/wallet/withdraw/chiliz/amount`);
					}}
				/>
			</ButtonSection>
		</S.Container>
	);
};
